import { polygonFirstRingToCorners } from '@/utils/defect/defectShapeOverlay';

/** 이미지 전체 대비 유효 가로(·세로) 비율 — 정사각형 중심 박스 (기본 75%) */
export const DEFAULT_AI_DEFECT_DISPLAY_LINEAR_RATIO = 0.75;

const FULL_LINEAR_THRESHOLD = 0.997;

export const AI_DEFECT_DISPLAY_PRESETS = Object.freeze({
  high: { preset: 'high', linearRatio: 0.5 },
  medium: { preset: 'medium', linearRatio: DEFAULT_AI_DEFECT_DISPLAY_LINEAR_RATIO },
  low: { preset: 'low', linearRatio: null }
});

const STORAGE_KEY_LINEAR = 'solvit.aiDefectDisplayLinearRatio';
const STORAGE_KEY_PRESET = 'solvit.aiDefectDisplayPreset';

export const DEFAULT_AI_DEFECT_DISPLAY_PREFS = Object.freeze({
  preset: 'medium',
  linearRatio: DEFAULT_AI_DEFECT_DISPLAY_LINEAR_RATIO
});

/**
 * @param {string|number|null|undefined} reportId
 * @returns {{ linear: string, preset: string } | null}
 */
export function aiDefectDisplayStorageKeysForReport(reportId) {
  if (reportId == null || reportId === '') return null;
  const id = String(reportId).trim();
  if (!id) return null;
  return {
    linear: `${STORAGE_KEY_LINEAR}.${id}`,
    preset: `${STORAGE_KEY_PRESET}.${id}`
  };
}

/**
 * @param {string} presetKey
 * @param {string} linearKey
 * @returns {{ preset: string, linearRatio: number | null }}
 */
function parseAiDefectDisplayPrefsFromKeys(presetKey, linearKey) {
  const preset = localStorage.getItem(presetKey);
  const raw = localStorage.getItem(linearKey);
  if (preset && AI_DEFECT_DISPLAY_PRESETS[preset]) {
    return {
      preset,
      linearRatio: linearRatioFromPreset(preset)
    };
  }
  if (raw === 'null' || raw === '') {
    return { preset: 'low', linearRatio: null };
  }
  const n = Number(raw);
  if (Number.isFinite(n) && n > 0 && n <= 1) {
    const linearRatio = migrateLegacyAreaLinearRatio(n);
    return { preset: presetFromLinearRatio(linearRatio), linearRatio };
  }
  return null;
}

/**
 * @param {string} presetKey
 * @param {string} linearKey
 * @param {string} preset
 * @param {number | null} linearRatio
 */
function writeAiDefectDisplayPrefsToKeys(presetKey, linearKey, preset, linearRatio) {
  if (preset) {
    localStorage.setItem(presetKey, preset);
  }
  if (linearRatio == null) {
    localStorage.setItem(linearKey, 'null');
  } else {
    localStorage.setItem(linearKey, String(linearRatio));
  }
}

/**
 * @param {unknown} defect
 * @returns {boolean}
 */
export function isAiDefectPendingDisplayFilter(defect) {
  if (!defect || typeof defect !== 'object') return false;
  const way = defect.detectWay;
  const wayStr = typeof way === 'string' ? way : way?.name || way?.toString?.() || '';
  if (String(wayStr).toUpperCase() !== 'AI') return false;
  const confirmed = defect.mlDefectConfirmed;
  if (confirmed == null || confirmed === '') return true;
  return String(confirmed).toUpperCase() === 'NO';
}

/**
 * @param {{ type?: string, coordinates?: unknown }} [geom]
 * @returns {{ x: number, y: number } | null}
 */
export function getDefectShapeCenterPx(geom) {
  const corners = polygonFirstRingToCorners(geom);
  if (!corners.length) return null;
  let sumX = 0;
  let sumY = 0;
  let n = 0;
  corners.forEach(pt => {
    if (!pt || pt.length < 2) return;
    sumX += Number(pt[0]);
    sumY += Number(pt[1]);
    n += 1;
  });
  if (n === 0) return null;
  return { x: sumX / n, y: sumY / n };
}

/**
 * 이미지 중심에 맞춘 정사각형(선형 비율 r) 안에 결함 중심이 들어가는지.
 * @param {number|null|undefined} linearRatio null·≥임계값이면 전체 표시
 */
export function isDefectCenterInDisplayRegion(defect, imgW, imgH, linearRatio) {
  if (linearRatio == null || linearRatio >= FULL_LINEAR_THRESHOLD) {
    return true;
  }
  if (!isAiDefectPendingDisplayFilter(defect)) {
    return true;
  }
  const w = Math.max(1, Number(imgW) || 640);
  const h = Math.max(1, Number(imgH) || 512);
  const center = getDefectShapeCenterPx(defect.defectShapeGeom);
  if (!center) {
    return false;
  }
  const r = Math.min(1, Math.max(0.04, Number(linearRatio)));
  const halfW = (r * w) / 2;
  const halfH = (r * h) / 2;
  const cx = w / 2;
  const cy = h / 2;
  return (
    center.x >= cx - halfW &&
    center.x <= cx + halfW &&
    center.y >= cy - halfH &&
    center.y <= cy + halfH
  );
}

/**
 * @param {unknown[]} defects
 * @param {number} imgW
 * @param {number} imgH
 * @param {number|null|undefined} linearRatio
 */
export function filterDefectsForOperatorDisplay(defects, imgW, imgH, linearRatio) {
  if (!Array.isArray(defects)) return [];
  return defects.filter(d => isDefectCenterInDisplayRegion(d, imgW, imgH, linearRatio));
}

/**
 * 열화상 목록·지도 WFS 가시성용 AI 결함 PK 집합.
 * @param {Array<{ id?: number, imgWidth?: number, imgHeight?: number, moduleDefects?: unknown[] }>} thermalImages
 * @param {number|null|undefined} linearRatio
 * @returns {Set<number>}
 */
export function buildVisibleAiDefectIdSet(thermalImages, linearRatio) {
  const ids = new Set();
  if (!Array.isArray(thermalImages)) return ids;
  for (const img of thermalImages) {
    const w = img?.imgWidth;
    const h = img?.imgHeight;
    const list = img?.moduleDefects || [];
    for (const d of list) {
      const pk = Number(d?.id);
      if (!Number.isFinite(pk) || pk <= 0) continue;
      if (isDefectCenterInDisplayRegion(d, w, h, linearRatio)) {
        ids.add(pk);
      }
    }
  }
  return ids;
}

export function linearRatioFromPreset(preset) {
  const key = preset && AI_DEFECT_DISPLAY_PRESETS[preset] ? preset : 'medium';
  return AI_DEFECT_DISPLAY_PRESETS[key].linearRatio;
}

export function presetFromLinearRatio(linearRatio) {
  if (linearRatio == null || linearRatio >= FULL_LINEAR_THRESHOLD) {
    return 'low';
  }
  const entries = Object.entries(AI_DEFECT_DISPLAY_PRESETS);
  let best = 'medium';
  let bestDiff = Infinity;
  for (const [name, cfg] of entries) {
    if (cfg.linearRatio == null) continue;
    const diff = Math.abs(cfg.linearRatio - linearRatio);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = name;
    }
  }
  return best;
}

/** @deprecated 전역 키 — 신규는 {@link loadAiDefectDisplayPrefsForReport} 사용 */
export function loadAiDefectDisplayPrefsFromStorage() {
  try {
    const parsed = parseAiDefectDisplayPrefsFromKeys(STORAGE_KEY_PRESET, STORAGE_KEY_LINEAR);
    if (parsed) return parsed;
  } catch (e) {
    console.warn('[aiDefectDisplayRegion] load prefs failed', e);
  }
  return { ...DEFAULT_AI_DEFECT_DISPLAY_PREFS };
}

/**
 * 보고서별 저장값 → 없으면 예전 전역 키 → 기본값(medium).
 * @param {string|number|null|undefined} reportId
 */
export function loadAiDefectDisplayPrefsForReport(reportId) {
  const keys = aiDefectDisplayStorageKeysForReport(reportId);
  if (!keys) {
    return { ...DEFAULT_AI_DEFECT_DISPLAY_PREFS };
  }
  try {
    const perReport = parseAiDefectDisplayPrefsFromKeys(keys.preset, keys.linear);
    if (perReport) return perReport;
    const legacy = parseAiDefectDisplayPrefsFromKeys(STORAGE_KEY_PRESET, STORAGE_KEY_LINEAR);
    if (legacy) return legacy;
  } catch (e) {
    console.warn('[aiDefectDisplayRegion] load prefs for report failed', e);
  }
  return { ...DEFAULT_AI_DEFECT_DISPLAY_PREFS };
}

/** @deprecated 전역 키 — 신규는 {@link saveAiDefectDisplayPrefsForReport} 사용 */
export function saveAiDefectDisplayPrefsToStorage(preset, linearRatio) {
  try {
    writeAiDefectDisplayPrefsToKeys(STORAGE_KEY_PRESET, STORAGE_KEY_LINEAR, preset, linearRatio);
  } catch (e) {
    console.warn('[aiDefectDisplayRegion] save prefs failed', e);
  }
}

/**
 * @param {string|number} reportId
 * @param {string} preset
 * @param {number | null} linearRatio
 */
export function saveAiDefectDisplayPrefsForReport(reportId, preset, linearRatio) {
  const keys = aiDefectDisplayStorageKeysForReport(reportId);
  if (!keys) return;
  try {
    writeAiDefectDisplayPrefsToKeys(keys.preset, keys.linear, preset, linearRatio);
  } catch (e) {
    console.warn('[aiDefectDisplayRegion] save prefs for report failed', e);
  }
}

/** 면적 기준(√r)으로 저장된 구버전 값 → 가로 비율로 변환 */
export function migrateLegacyAreaLinearRatio(linearRatio) {
  const legacyPairs = [
    [Math.sqrt(0.3), 0.3],
    [Math.sqrt(0.5), 0.5]
  ];
  for (const [legacy, widthRatio] of legacyPairs) {
    if (Math.abs(linearRatio - legacy) < 0.02) {
      return widthRatio;
    }
  }
  return linearRatio;
}

/** 선형 비율 r → 가로(및 세로) 길이가 이미지의 r×100% */
export function widthPercentFromLinearRatio(linearRatio) {
  if (linearRatio == null || linearRatio >= FULL_LINEAR_THRESHOLD) {
    return 100;
  }
  return Math.round(linearRatio * 1000) / 10;
}

/** 가로 % 입력 → picker·서버용 선형 비율 (100=전체 → null) */
export function linearRatioFromWidthPercent(percent) {
  const n = Number(percent);
  if (!Number.isFinite(n) || n >= 100) {
    return null;
  }
  if (n <= 4) {
    return 0.04;
  }
  return Math.min(FULL_LINEAR_THRESHOLD - 0.001, n / 100);
}

/** @deprecated 가로 비율 표기로 변경 — {@link widthPercentFromLinearRatio} 사용 */
export function areaPercentFromLinearRatio(linearRatio) {
  return widthPercentFromLinearRatio(linearRatio);
}
